import React, {FC, useRef, useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Preferences} from '@/app/interfaces/preferences';
import {languageNames} from '@/common/constants/language';
import {ClockType, PressureUnit, SpeedUnit, TemperatureUnit} from '@/common/constants/units';
import {StoreThunkDispatch} from '@/app/store';
import {selectDomainConfig, selectPreferences} from '@/app/store/config/selectors';
import {updatePreferencesAction} from '@/app/store/config/actions';
import {useOutsideClick} from '@/common/helpers/hooks/useOutsideClick';
import {useLocalizer} from '@/common/services/localizer';
import {useErrorInformerPush} from '@/common/services/ErrorInformer';
import {SettingsRow} from '../SettingsRow';
import styles from './SettingsMenu.css';

interface SettingsMenuProps {
	onClose(): void;
}

export const SettingsMenu: FC<SettingsMenuProps> = ({onClose}) => {
	const preferences = useSelector(selectPreferences);
	const {supportedLanguages} = useSelector(selectDomainConfig);

	const rootRef = useRef<HTMLDivElement>(null);

	useOutsideClick(rootRef, onClose);

	const t = useLocalizer();
	const errorInformerPush = useErrorInformerPush();

	const optionNameTransformers = useMemo(
		() => ({
			lang(option: string) {
				return languageNames[option];
			},
			temperatureUnit(option: string) {
				return t(`settings.temperatureUnit.${option}`);
			},
			speedUnit(option: string) {
				return t(`settings.speedUnit.${option}`);
			},
			pressureUnit(option: string) {
				return t(`settings.pressureUnit.${option}`);
			},
			clockType(option: string) {
				return new Date()
					.toLocaleTimeString('en', {
						hour12: option === ClockType['12h'],
						minute: 'numeric',
						hour: 'numeric',
					})
					.toLowerCase();
			},
		}),
		[t.language],
	);

	const dispatch = useDispatch<StoreThunkDispatch>();

	const handleValueChange = useCallback(
		async (field: keyof Preferences, value: string) => {
			if (preferences[field] !== value) {
				try {
					await dispatch(updatePreferencesAction({[field]: value}));
				} catch (error) {
					errorInformerPush(error);
					onClose();
					throw error;
				}
			}

			onClose();
		},
		[onClose],
	);

	return (
		<div ref={rootRef} className={styles.root}>
			<SettingsRow
				field='lang'
				title={t(`settings.fields.lang`)}
				value={t.language}
				options={supportedLanguages}
				optionNameTransformer={optionNameTransformers.lang}
				onChange={handleValueChange}
			/>
			<SettingsRow
				field='temperatureUnit'
				title={t(`settings.fields.temperature`)}
				value={preferences.temperatureUnit}
				options={Object.values(TemperatureUnit)}
				optionNameTransformer={optionNameTransformers.temperatureUnit}
				onChange={handleValueChange}
			/>
			<SettingsRow
				field='speedUnit'
				title={t(`settings.fields.windSpeed`)}
				value={preferences.speedUnit}
				options={Object.values(SpeedUnit)}
				optionNameTransformer={optionNameTransformers.speedUnit}
				onChange={handleValueChange}
			/>
			<SettingsRow
				field='pressureUnit'
				title={t(`settings.fields.pressure`)}
				value={preferences.pressureUnit}
				options={Object.values(PressureUnit)}
				optionNameTransformer={optionNameTransformers.pressureUnit}
				onChange={handleValueChange}
			/>
			<SettingsRow
				field='clockType'
				title={t(`settings.fields.clockType`)}
				value={preferences.clockType}
				options={Object.values(ClockType)}
				optionNameTransformer={optionNameTransformers.clockType}
				onChange={handleValueChange}
			/>
		</div>
	);
};

SettingsMenu.displayName = 'SettingsMenu';
